<template>
  <div class="portal-root">
    <!-- Soft Dynamic Background -->
    <div class="bg-elements">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
    </div>
    
    <div class="portal-overlay">
      <!-- Floating Header -->
      <header class="portal-header">
        <div class="brand-box">
          <img src="@/assets/img/logo.png" alt="Sundava" class="header-logo" style="height: 150px; width: auto;">
        </div>
        
        <div class="header-actions">
           <!-- Smart Clock & Weather Widget -->
           <div class="smart-widget-glass">
               <div class="widget-icon">
                <i :class="weatherData.icon" class="animate-pulse-slow"></i>
              </div>
              <div class="widget-details">
                <div class="temp-row">
                  <span class="temperature">{{ weatherData.temp }}°C</span>
                  <span class="condition">{{ weatherData.text }}</span>
                </div>
                <div class="time-row">
                  <span class="digital-clock text-dark">{{ currentTime }}</span>
                  <span class="divider"></span>
                  <span class="calendar-date text-uppercase">{{ currentDate }}</span>
                </div>
              </div>
           </div>

           <!-- User Profile Section -->
           <div v-if="currentUser" class="user-profile-glass shadow-sm">
              <div class="avatar-circle">
                {{ (currentUser.full_name || currentUser.name || 'U').charAt(0).toUpperCase() }}
              </div>
              <div class="user-meta d-none d-md-block">
                <div class="user-name text-dark">{{ currentUser.full_name || currentUser.name }}</div>
                <div class="user-email text-muted text-truncate" style="max-width: 150px;">{{ currentUser.email }}</div>
              </div>
              <button @click="logout" class="logout-glass-btn" title="Logout">
                <vue-feather type="log-out" size="18"></vue-feather>
              </button>
           </div>
           <router-link v-else to="/login" class="login-btn-premium">
             Masuk
           </router-link>
        </div>
      </header>

      <!-- Main Hub -->
      <main class="portal-main-content">
        <div class="welcome-hero text-center mb-5">
           <h1 class="hero-title text-dark">Welcome to <span class="text-gradient">Sundava Cloud</span></h1>
           <p class="hero-subtitle">Unified Operations Hub & Strategic Monitoring Ecosystem</p>
        </div>

        <div class="android-hub-grid">
           <!-- SSP App -->
           <div 
            v-if="$hasAppAccess('ssp')"
            class="android-app-item" 
            @click="enterApp('ssp')"
           >
              <div class="android-icon-box shadow-premium">
                 <img src="@/assets/img/ssp-logo-light.png" alt="SSP" class="android-img">
              </div>
              <span class="android-label">SSP ADMIN</span>
           </div>

           <!-- User Management App (Only for Admin) -->
           <div 
             v-if="currentUser && currentUser.role === 'admin'" 
             class="android-app-item" 
             @click="router.push('/admin-management')"
           >
              <div class="android-icon-box shadow-sm bg-danger-soft">
                 <vue-feather type="settings" class="text-danger"></vue-feather>
              </div>
              <span class="android-label text-truncate">User Management</span>
           </div>
        </div>
      </main>

      <!-- Immersive Footer -->
      <footer class="portal-footer-minimal">
         <div class="footer-content">
            <span class="copy">&copy; {{ year }} PT Sundava Agro Indonesia</span>
            <span class="separator d-none d-md-inline">|</span>
            <span class="tagline d-none d-md-inline">Empowering Sustainable Agriculture</span>
         </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import authService from "@/services/authService";

export default {
  setup() {
    const router = useRouter();
    const currentTime = ref('');
    const currentDate = ref('');
    const currentUser = ref(null);
    const weatherData = ref({
      temp: '--',
      text: 'Memuat...',
      icon: 'fas fa-spinner fa-spin'
    });

    const year = new Date().getFullYear();

    const updateTime = () => {
      const now = new Date();
      currentTime.value = now.toLocaleTimeString('id-ID', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false 
      });
      
      const options = { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' };
      currentDate.value = now.toLocaleDateString('id-ID', options);
    };

    const fetchWeather = async (lat = -6.9175, lon = 107.6191) => {
      try {
        const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
        const data = await res.json();
        const code = data.current_weather.weathercode;
        
        const weatherMap = {
          0: { text: 'Cerah', icon: 'fas fa-sun text-warning' },
          1: { text: 'Cerah Berawan', icon: 'fas fa-cloud-sun text-warning' },
          2: { text: 'Berawan', icon: 'fas fa-cloud text-secondary' },
          3: { text: 'Mendung', icon: 'fas fa-cloud text-muted' },
          45: { text: 'Berkabut', icon: 'fas fa-smog' },
          51: { text: 'Gerimis', icon: 'fas fa-cloud-rain text-primary' },
          61: { text: 'Hujan Ringan', icon: 'fas fa-cloud-showers-heavy text-primary' },
          80: { text: 'Hujan', icon: 'fas fa-cloud-showers-heavy text-primary' },
          95: { text: 'Badai Guntur', icon: 'fas fa-bolt text-warning' }
        };

        const weather = weatherMap[code] || { text: 'Cerah Berawan', icon: 'fas fa-cloud-sun text-warning' };
        
        weatherData.value = {
          temp: Math.round(data.current_weather.temperature),
          text: weather.text,
          icon: weather.icon
        };
      } catch (e) {
        console.error("Failed to fetch weather", e);
        weatherData.value = { temp: '28', text: 'Cerah Berawan', icon: 'fas fa-cloud-sun text-warning' };
      }
    };



    let timer;
    onMounted(() => {
      updateTime();
      
      // Try GPS first
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
          () => fetchWeather() // Fallback to Bandung/Default
        );
      } else {
        fetchWeather();
      }


      
      timer = setInterval(updateTime, 1000);
      setInterval(() => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
            () => fetchWeather()
          );
        } else {
          fetchWeather();
        }
      }, 600000); // 10 mins

      
      const session = localStorage.getItem('currentUser');
      if (session) {
        currentUser.value = JSON.parse(session);
        
        // Security check: Must change password (safely)
        if (currentUser.value && currentUser.value.must_change_password) {
          router.push('/change-password');
        }
      }

      document.body.classList.add('portal-page-v3');
    });

    onUnmounted(() => {
      clearInterval(timer);
      document.body.classList.remove('portal-page-v3');
    });

    const enterApp = (app) => {
      if (!currentUser.value) {
        router.push('/login');
        return;
      }
      if (app === 'ssp') {
        router.push('/dashboard/admin-dashboard');
      }
    };

    const logout = () => {
      authService.logout();
      currentUser.value = null;
    };

    return {
      currentTime,
      currentDate,
      currentUser,
      weatherData,
      year,
      enterApp,
      logout,
      router
    };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Outfit:wght@400;600;800&display=swap');

.portal-root {
  min-height: 100vh;
  width: 100vw;
  background-color: #f8fafc; /* Soft Light background */
  font-family: 'Inter', sans-serif;
  color: #334155;
  overflow-x: hidden;
  position: relative;
}

/* --- Soft Decorative Background --- */
.bg-elements {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  animation: move-blobs 25s infinite alternate ease-in-out;
}

.blob-1 {
  width: 600px;
  height: 600px;
  background: #dcfce7; /* Very light green */
  top: -10%;
  right: -5%;
}

.blob-2 {
  width: 500px;
  height: 500px;
  background: #f0f9ff; /* Very light blue */
  bottom: -5%;
  left: -5%;
  animation-delay: -5s;
}

.blob-3 {
  width: 300px;
  height: 300px;
  background: #fdf2f8; /* Very light pink */
  top: 20%;
  left: 20%;
}

@keyframes move-blobs {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(100px, 50px) scale(1.1); }
}

/* --- Layout Overlay --- */
.portal-overlay {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 0 40px;
}

/* --- Header Styling --- */
.portal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 20px 0;
  flex-wrap: wrap;
  gap: 15px;
}

.header-logo {
  height: 60px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Smart Widget - Light Mode */
.smart-widget-glass {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 1);
  box-shadow: 0 10px 30px rgba(0,0,0,0.03);
  padding: 10px 20px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.temp-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Outfit', sans-serif;
  font-weight: 800;
  font-size: 16px;
}

.time-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
  font-weight: 700;
}

.divider {
  width: 1px;
  height: 10px;
  background: #e2e8f0;
}

/* User Profile - Light Mode */
.user-profile-glass {
  background: #fff;
  border: 1px solid #f1f5f9;
  padding: 6px 6px 6px 15px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-circle {
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: white;
  font-family: 'Outfit', sans-serif;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.logout-glass-btn {
  background: #fef2f2;
  border: none;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.logout-glass-btn:hover {
  background: #ef4444;
  color: white;
}

.login-btn-premium {
  background: #10b981;
  color: white;
  padding: 12px 32px;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.15);
}

.login-btn-premium:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(16, 185, 129, 0.25);
}

/* --- Main Content Styling --- */
.portal-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.hero-title {
  font-family: 'Outfit', sans-serif;
  font-size: 48px;
  font-weight: 800;
  letter-spacing: -1.8px;
  margin-bottom: 5px;
}

.text-gradient {
  background: linear-gradient(135deg, #059669, #047857);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 18px;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
}

.tile-title {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 1px;
}

/* --- Android Style Hub --- */
.android-hub-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 600px;
  margin: 20px auto 40px auto;
}

.android-app-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.android-app-item:hover {
  transform: scale(1.1);
}

.android-app-item.disabled {
  cursor: default;
}

.android-icon-box {
  width: 85px;
  height: 85px;
  background: white;
  border-radius: 22px; /* iOS/Android squircle */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.03);
}

.android-app-item:hover .android-icon-box {
  box-shadow: 0 15px 30px rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.1);
}

.android-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.android-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-align: center;
  font-family: 'Inter', sans-serif;
}

.android-app-item:hover .android-label {
  color: #10b981;
}

/* --- Footer Styling --- */
.portal-footer-minimal {
  padding: 30px 0;
  font-size: 12px;
  color: #94a3b8;
}

.footer-content {
  display: flex;
  gap: 15px;
  align-items: center;
}

@media (max-width: 991.98px) {
  .dashboard-hub {
    grid-template-columns: 1fr;
  }
  .hero-title {
    font-size: 32px;
  }
}

@media (max-width: 767.98px) {
  .portal-overlay {
    padding: 0 15px;
  }
  .portal-header {
    padding: 10px 0;
    justify-content: space-between;
    flex-wrap: nowrap; /* Keep on one line if possible */
    gap: 10px;
  }
  .header-logo {
    height: 120px !important; /* Maximized for mobile branding */
  }
  .header-actions {
    gap: 8px;
  }
  .smart-widget-glass {
    padding: 6px 10px;
    border-radius: 12px;
  }
  .widget-icon {
    font-size: 16px;
  }
  .temp-row .temperature {
    font-size: 13px;
  }
  .temp-row .condition, .time-row {
    display: none; /* Hide extra details on mobile for compactness */
  }
  .user-profile-glass {
    padding: 4px;
    gap: 5px;
  }
  .avatar-circle {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  .user-meta {
    display: none !important; /* Always hide name/email on small mobile */
  }
  .logout-glass-btn {
    width: 32px;
    height: 32px;
  }
  .hero-title {
    font-size: 26px;
    margin-top: 20px;
  }
  .hero-subtitle {
    font-size: 13px;
  }
  .dashboard-hub {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  .hub-card {
    background: transparent;
    border: none;
    box-shadow: none !important;
    padding: 0;
    width: auto;
  }
  .hub-card:hover {
    transform: scale(1.05); /* Soft bounce on hover for mobile */
  }
  .card-status-bar, .hub-card-footer {
    display: none !important;
  }
  .hub-card-body {
    flex-direction: column !important;
    gap: 12px;
    align-items: center;
    text-align: center;
    margin-bottom: 0px;
  }
  .info-grid {
    display: none !important;
  }
  .app-icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 20px; /* Android squircle style */
    box-shadow: 0 10px 25px rgba(0,0,0,0.08);
    margin: 0;
  }
  .app-description {
    display: none;
  }
  .app-title {
    font-size: 14px;
    font-weight: 700;
    color: #334155 !important;
    margin-top: 5px;
  }
  .app-info {
    text-align: center;
  }
  /* Shorten the text for mobile */
  .app-title::after {
    display: none;
  }
  
  .welcome-hero {
    margin-bottom: 40px !important;
  }
  .hero-title {
    font-size: 26px;
    margin-top: 10px;
  }
  
  .android-hub-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 20px; /* More vertical breathing room */
    padding: 20px 0;
  }
  
  .android-icon-box {
    width: 100px; /* Much larger icons */
    height: 100px;
    padding: 20px;
    border-radius: 28px;
    box-shadow: 0 15px 35px rgba(0,0,0,0.08) !important;
  }
  
  .android-label {
    font-size: 14px; /* More readable text */
    font-weight: 700;
  }

  .login-btn-premium {
    padding: 10px 20px; /* More compact on mobile */
    font-size: 14px;
  }
}
</style>
